import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { PublicClientApplication, InteractionStatus, type AccountInfo } from '@azure/msal-browser'
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react'
import { getAuthConfig, loginRequest, apiConfig } from '@/config/auth'
import { config } from '@/lib/config'
import { Loader2 } from 'lucide-react'

// MSAL instance management
let msalInstance: PublicClientApplication | null = null
let initPromise: Promise<void> | null = null

const getMsalInstance = () => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(getAuthConfig())
    initPromise = msalInstance.initialize()
  }
  return msalInstance
}

const ensureMsalInitialized = async () => {
  if (initPromise) {
    await initPromise
  }
}

// Export for use in API interceptor
export const getMsalInstanceForApi = () => msalInstance

// Backend user profile type
interface UserProfile {
  email: string
  name: string
  role: 'SuperUser' | 'Admin' | 'User'
  permissions: string[]
  is_active: boolean
}

// Auth Context Type
interface AuthContextType {
  user: (AccountInfo & { role?: string; permissions?: string[] }) | null
  userProfile: UserProfile | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => Promise<string | null>
  refreshUserProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Inner provider that uses MSAL hooks
function AuthProviderInner({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [user, setUser] = useState<(AccountInfo & { role?: string; permissions?: string[] }) | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Fetch user profile from backend
  const fetchUserProfile = useCallback(async (accessToken: string) => {
    try {
      // Use the same base URL as apiClient (already includes /api suffix)
      const apiBaseUrl = config.api.baseUrl
      const url = `${apiBaseUrl}/v1/users/me`
      console.log('[Auth] Fetching user profile from:', url)

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      })

      console.log('[Auth] Profile response status:', response.status)

      if (response.ok) {
        const data = await response.json()
        console.log('[Auth] Profile data:', data)

        if (data.has_access && data.user) {
          setUserProfile(data.user)
          // Also update the user object with role for convenience
          setUser(prev => prev ? { ...prev, role: data.user.role, permissions: data.user.permissions } : null)
          console.log('[Auth] User role set to:', data.user.role)
        } else {
          console.log('[Auth] User does not have access or no user data')
        }
      } else {
        console.error('[Auth] Profile fetch failed:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('[Auth] Failed to fetch user profile:', error)
    }
  }, [])

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await ensureMsalInitialized()

        // Handle redirect promise
        try {
          const response = await instance.handleRedirectPromise()
          if (response) {
            setUser(response.account)
          }
        } catch (redirectError: unknown) {
          const error = redirectError as { errorCode?: string }
          if (!error?.errorCode?.includes('no_token_request_cache_error')) {
            console.error('Error handling redirect:', redirectError)
          }
        }

        // Check for existing accounts
        if (accounts.length > 0) {
          setUser(accounts[0])

          // Acquire token and fetch user profile
          try {
            const tokenResponse = await instance.acquireTokenSilent({
              scopes: [apiConfig.backendApiScope],
              account: accounts[0],
            })
            // Fetch user profile from backend
            await fetchUserProfile(tokenResponse.accessToken)
          } catch {
            // Token not available, will be acquired on demand
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
      } finally {
        setTimeout(() => setIsLoading(false), 100)
      }
    }

    initializeAuth()
  }, [instance, accounts, inProgress, fetchUserProfile])

  const login = async () => {
    try {
      await ensureMsalInitialized()
      const loginResponse = await instance.loginPopup(loginRequest)
      setUser(loginResponse.account)

      // Fetch user profile after login
      if (loginResponse.accessToken) {
        await fetchUserProfile(loginResponse.accessToken)
      } else {
        // Acquire token and fetch profile
        try {
          const tokenResponse = await instance.acquireTokenSilent({
            scopes: [apiConfig.backendApiScope],
            account: loginResponse.account,
          })
          await fetchUserProfile(tokenResponse.accessToken)
        } catch {
          // Will be fetched on next load
        }
      }
    } catch (error) {
      console.error('Login failed:', error)
      // Fallback to redirect
      await instance.loginRedirect(loginRequest)
    }
  }

  const refreshUserProfile = async () => {
    const accountToUse = user || accounts[0]
    if (!accountToUse) return

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: [apiConfig.backendApiScope],
        account: accountToUse,
      })
      await fetchUserProfile(tokenResponse.accessToken)
    } catch (error) {
      console.error('Failed to refresh user profile:', error)
    }
  }

  const logout = async () => {
    try {
      await ensureMsalInitialized()
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
        mainWindowRedirectUri: window.location.origin,
      })
      setUser(null)
    } catch (error) {
      console.error('Logout failed:', error)
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      })
    }
  }

  const getAccessToken = async (): Promise<string | null> => {
    const accountToUse = user || accounts[0]
    if (!accountToUse) return null

    try {
      await ensureMsalInitialized()
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: [apiConfig.backendApiScope],
        account: accountToUse,
      })
      return tokenResponse.accessToken
    } catch (error) {
      console.error('Silent token acquisition failed:', error)
      try {
        const tokenResponse = await instance.acquireTokenPopup({
          scopes: [apiConfig.backendApiScope],
          account: accountToUse,
        })
        return tokenResponse.accessToken
      } catch (popupError) {
        console.error('Token acquisition failed:', popupError)
        return null
      }
    }
  }

  const isActuallyLoading =
    isLoading ||
    (inProgress !== InteractionStatus.None && inProgress !== InteractionStatus.HandleRedirect)

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        isAuthenticated,
        isLoading: isActuallyLoading,
        login,
        logout,
        getAccessToken,
        refreshUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// Main AuthProvider
export function AuthProvider({ children }: { children: ReactNode }) {
  const [msalReady, setMsalReady] = useState(false)
  const instance = getMsalInstance()

  useEffect(() => {
    ensureMsalInitialized()
      .then(() => setMsalReady(true))
      .catch((error) => console.error('MSAL initialization failed:', error))
  }, [])

  if (!msalReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Initializing authentication...</p>
        </div>
      </div>
    )
  }

  return (
    <MsalProvider instance={instance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  )
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
